import { DateTime } from "luxon";
import React, { useEffect, useState, useContext } from "react";
import { Form, Modal, Row, Col, Button, ToastContainer, Toast } from "react-bootstrap";
import { useMsal } from "@azure/msal-react";
import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import CreatableSelect from 'react-select/creatable';
import AsyncCreatableSelect from 'react-select/async-creatable';
import debounce from 'debounce-promise';

import { departments, workPlaces, shiftTypes, advertisingTypes, incomeTypes, salaryTimeFrames, contractTypes, benefits, laboursHire } from "../dropdowns";
import { searchUsers, createRequestAPI, searchPositions } from "../graph";
import { humanizeDate } from '../helper/date';
import { loginRequest } from "../authConfig";
import { Context } from "../Context";


import "../styles/ModalViewDetailsRequest.css";

/**
 * Renders information about the user obtained from MS Graph
 * @param props 
 */
export const ModalViewDetailsRequest = (props) => {
    const { instance, accounts } = useMsal();
    const [ request, setRequest ] = useState(props.request);
    const [ validated, setValidated ] = useState(false);
    const [ showToast, setShowToast ] = useState(false);
    const [ messageAPI, setMessageAPI ] = useState('');
    const [, setIsLoading] = useContext(Context);

    useEffect(() => {
        const initState = {};
        if (!props.request) {
            initState.department = props.department;
            initState.workPlace = 'Office';
            initState.incomeType = 'Salary';
            initState.salaryTimeFrame = 'year';
            initState.contractType = 'Permanent';
            initState.shiftType = 'day';
            initState.salaryFrom = 0;
            initState.salaryTo = 0;
        }
        setRequest({...props.request, ...initState});
        setValidated(false);
    }, [props.request])

    const getDateOnly = (fullDate) => {
        if (fullDate) {
            return DateTime.fromISO(fullDate).startOf('day').toISODate()
        }
        return '';
    }

    const customStyles = {
        control: base => ({
          ...base,
          height: 'auto',
          minHeight: 29,
          fontSize: 14,
        })
    };

    const customStylesRed = {
        control: base => ({
          ...base,
          height: 'auto',
          minHeight: 29,
          fontSize: 14,
          borderColor: 'red'
        })
    };

    const capitalize = (text) => {
        const str = text.split('.');
        let result = '';
      
        str.forEach(element => {
          const lower = element.toLowerCase();
          result = `${result} ${element.charAt(0).toUpperCase()}${lower.slice(1)}`;
        });
      
        return result.slice(1);
    };

    const cleanNameFromIdentity = (identity) => {
        if (!identity) {
            return [];
        } 
        const [name] = identity.split('@');
        return {label: capitalize(name), value: identity};
    }

    const changeValue = (v) => {
        let value = v.currentTarget.value;

        if (['fixedStartDate', 'fixedEndDate'].includes(v.currentTarget.id)) {
            value = DateTime.fromFormat(value, 'yyyy-MM-dd').toISO()
        }

        if (['advertising', 'benefits'].includes(v.currentTarget.id)) {
            value = value.map(v => v.value);
        }

        if (['panel1', 'panel2', 'replacement'].includes(v.currentTarget.id)) {
            value = value ? value.value : undefined;
        }

        if (v.currentTarget.id === 'contractType') {
            if (value === 'Permanent') {
                setRequest({...request, [v.currentTarget.id]: value, fixedStartDate: undefined, fixedEndDate: undefined, labourHire: undefined});
            } else if (value === 'Fixed') {
                setRequest({...request, [v.currentTarget.id]: value, labourHire: undefined});
            } else if (value === 'Casual') {
                setRequest({...request, [v.currentTarget.id]: value, fixedStartDate: undefined, fixedEndDate: undefined, labourHire: 'Fulton Hogan'});
            }
        } else {
            setRequest({...request, [v.currentTarget.id]: value})
        }
    };

    const submitRequest = (event) => {
        const form = event.currentTarget;
        event.preventDefault();
        event.stopPropagation();

        if (!confirm('Are you sure you want to create this request?')) {
            return;
        }

        if (form.checkValidity() && request?.position) {
            // Silently acquires an access token which is then attached to a request for MS Graph data
            instance.acquireTokenSilent({
                ...loginRequest,
                account: accounts[0]
            }).then((response) => {
                setIsLoading(true);

                createRequestAPI(response.accessToken, request)
                    .then(() => {
                        props.onRequestCreated();
                        props.onClose();
                    })
                    .catch(error => {
                        setMessageAPI(`Error, ${error}`);
                        setShowToast(true);
                        setValidated(false);
                    })
                    .finally (() => {
                        setIsLoading(false);
                    });
            });
        } else {
            setValidated(true);
        }
    };

    const getUsers = async (inputValue, exclude) => {
        try {
            // Silently acquires an access token which is then attached to a request for MS Graph data
            const response = await instance.acquireTokenSilent({...loginRequest, account: accounts[0]});
            const users = await searchUsers(response.accessToken, inputValue);
            return users.length
                ? users.filter(u => u.mail !== exclude).map((option) => ({
                    label: `${option.givenName} ${option.surname}`,
                    value: option.mail,
                }))
            : [];
        } catch (e) {
            return [];
        }
    };

    const getPositions = async (inputValue) => {
        try {
            // Silently acquires an access token which is then attached to a request for MS Graph data
            const response = await instance.acquireTokenSilent({...loginRequest, account: accounts[0]});
            const positions = await searchPositions(response.accessToken, inputValue);
            return positions.length
                ? positions.map((option) => ({
                    label: option,
                    value: option,
                }))
            : [];
        } catch (e) {
            return [];
        }
    };

    const debouncedLoadUsers = debounce((inputValue, exclude) => getUsers(inputValue, exclude), 300, {
      leading: true
    });

    const debouncedLoadPositions = debounce(inputValue => getPositions(inputValue), 300, {
        leading: true
      });

    return (
        <>
            <Modal
                className="modalDetailsRequest"
                show={props.show}
                onHide={props.onClose}
                aria-labelledby="contained-modal-title-vcenter"
                centered
            >
                <Form noValidate validated={validated} onSubmit={submitRequest} >
                    <Modal.Header closeButton>
                        {request && request.id && (<Modal.Title>Requested by {cleanNameFromIdentity(request?.requestedBy).label} on {request?.createdOn ? humanizeDate(request?.createdOn.split('T')[0]) : ''}</Modal.Title>)}
                        {(!request || (request && !request.id)) && (<Modal.Title>Recruitment request</Modal.Title>)}
                    </Modal.Header>
                    <Modal.Body>
                        <Row className="mb-2">
                            <Form.Group as={Col} controlId="position">
                                <Form.Label>Position</Form.Label>
                                <AsyncCreatableSelect
                                    placeholder="Type a position"
                                    isClearable
                                    cacheOptions
                                    isDisabled={!props.editable || showToast}
                                    value={request?.position ? { label: request?.position, value: request?.position } : []}
                                    onChange={(v) => changeValue({currentTarget: { id: 'position', value: v ? v.value : ''}})}
                                    name="position"
                                    loadOptions={debouncedLoadPositions}
                                    className="basic-multi-select"
                                    classNamePrefix="select"
                                    styles={validated && !request?.position ? customStylesRed : customStyles}
                                />
                            </Form.Group>
                            <Form.Group as={Col} xs={5} sm={4} controlId="workPlace">
                                <Form.Label>Work place</Form.Label>
                                <Form.Select disabled={!props.editable || showToast} name="workPlaceOptions" required size="sm" aria-label="Work place" onChange={changeValue} value={request?.workPlace || ''} >
                                    {workPlaces.map(workPlace => (
                                        <option key={workPlace.value} value={workPlace.value}>{workPlace.label}</option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </Row>
                        <Row className="mb-2">
                            <Form.Group as={Col} controlId="description">
                                <Form.Label>Description</Form.Label>
                                <Form.Control as="textarea" required readOnly={!props.editable || showToast} onChange={changeValue} value={request?.description || ''} />
                            </Form.Group>
                        </Row>
                        <Row className="mb-2">
                            <Form.Group as={Col} sm={8} className="mb-sm-0 mb-2" controlId="department">
                                <Form.Label>Department</Form.Label>
                                <Form.Select disabled={!props.editable || showToast} name="department" required size="sm" aria-label="Department" onChange={changeValue} value={request?.department || ''} >
                                    {departments.map(department => (
                                        <option key={department.value} value={department.value}>{department.label}</option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                            <Form.Group as={Col} xs={6} sm={4} className="mb-sm-2" controlId="incomeType">
                                <Form.Label>Income</Form.Label>
                                <Form.Select disabled={!props.editable || showToast} name="incomeTypeOptions" required size="sm" aria-label="Income type" onChange={changeValue} value={request?.incomeType || ''} >
                                    {incomeTypes.map(incomeType => (
                                        <option key={incomeType.value} value={incomeType.value}>{incomeType.label}</option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                            <Form.Group as={Col} xs={6} sm={4} className="order-5 order-sm-4" controlId="salaryFrom">
                                <Form.Label>From (NZD)</Form.Label>
                                <Form.Control type="number" min={0} required readOnly={!props.editable || showToast} onChange={changeValue} value={request?.salaryFrom || 0} />
                            </Form.Group>
                            <Form.Group as={Col} xs={6} sm={4} className="order-6 order-sm-5" controlId="salaryTo">
                                <Form.Label>To (NZD)</Form.Label>
                                <Form.Control type="number" min={Math.max(0, request?.salaryFrom)} required readOnly={!props.editable || showToast} onChange={changeValue} value={request?.salaryTo || 0} />
                            </Form.Group>
                            <Form.Group as={Col} xs={6} sm={4} className="order-4 order-sm-6" controlId="salaryTimeFrame">
                                <Form.Label>Frequency</Form.Label>
                                <Form.Select disabled={!props.editable || showToast} name="salaryTimeFrameOptions" required size="sm" aria-label="Salary timeframe" onChange={changeValue} value={request?.salaryTimeFrame || ''} >
                                    {salaryTimeFrames.map(salaryTimeFrame => (
                                        <option key={salaryTimeFrame.value} value={salaryTimeFrame.value}>{salaryTimeFrame.label}</option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </Row>
                        <Row className="mb-2">
                            <Form.Group as={Col} xs={12} sm={8} controlId="advertising">
                                <Form.Label>Advertising</Form.Label>
                                <Select
                                    isMulti
                                    isDisabled={!props.editable || showToast}
                                    value={request?.advertising ? request?.advertising.map(v => ({label:v, value:v})) : []}
                                    onChange={(v) => changeValue({currentTarget: { id: 'advertising', value: v}})}
                                    name="advertisingOptions"
                                    options={advertisingTypes}
                                    className="basic-multi-select"
                                    classNamePrefix="select"
                                    styles={customStyles}
                                />
                            </Form.Group>
                            <Form.Group as={Col} xs={6} sm={4} className="mt-2 mt-sm-0" controlId="contractType">
                                <Form.Label>Contract</Form.Label>
                                <Form.Select disabled={!props.editable || showToast} name="contractTypeOptions" required size="sm" aria-label="contract type" onChange={changeValue} value={request?.contractType || ''} >
                                    {contractTypes.map(contractType => (
                                        <option key={contractType.value} value={contractType.value}>{contractType.label}</option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                            { request?.contractType === 'Fixed' && (
                                <>
                                    <Form.Group as={Col} xs={6} sm={4} className="mt-2" controlId="fixedStartDate">
                                        <Form.Label>Start date</Form.Label>
                                        <Form.Control min={new Date().toISOString().split("T")[0]} type="date" required={request?.contractType === 'Fixed'} pattern="\d{2}/\d{2}/\d{4}" readOnly={!props.editable || showToast} onChange={changeValue} value={getDateOnly(request?.fixedStartDate)} />
                                    </Form.Group>
                                    <Form.Group as={Col} xs={6} sm={4} className="mt-2" controlId="fixedEndDate">
                                        <Form.Label>End date</Form.Label>
                                        <Form.Control min={getDateOnly(request?.fixedStartDate)} type="date" required={request?.contractType === 'Fixed'} pattern="\d{2}/\d{2}/\d{4}" readOnly={!props.editable || showToast} onChange={changeValue} value={getDateOnly(request?.fixedEndDate)} />
                                    </Form.Group>
                                </>
                            )}
                            { request?.contractType === 'Casual' && (
                                <Form.Group as={Col} xs={6} sm={4} className="mt-2" controlId="labourHire">
                                    <Form.Label>Labour hire</Form.Label>
                                    <Form.Select disabled={!props.editable || showToast} name="labourHireOptions" required size="sm" aria-label="Labour hire" onChange={changeValue} value={request?.labourHire || ''} >
                                    {laboursHire.map(labourHire => (
                                        <option key={labourHire.value} value={labourHire.value}>{labourHire.label}</option>
                                    ))}
                                </Form.Select>                                
                            </Form.Group>
                            )}
                            <Form.Group as={Col} xs={6} sm={4} className="mt-2" controlId="shiftType">
                                <Form.Label>Shift type</Form.Label>
                                <Form.Select disabled={!props.editable || showToast} name="shiftTypeOptions" required size="sm" aria-label="Shift type" onChange={changeValue} value={request?.nightShift}>
                                    {shiftTypes.map(shiftType => (
                                        <option key={shiftType.value} value={shiftType.value}>{shiftType.label}</option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                            <Form.Group as={Col} className="mt-2" controlId="replacement">
                                <Form.Label>Replacement</Form.Label>
                                <AsyncSelect
                                    isClearable
                                    cacheOptions
                                    isDisabled={!props.editable || showToast}
                                    classNamePrefix="select"
                                    styles={customStyles}
                                    loadOptions={debouncedLoadUsers}
                                    onChange={(v) => changeValue({currentTarget: { id: 'replacement', value: v}})}
                                    placeholder={'Type a name'}
                                    value={cleanNameFromIdentity(request?.replacement)}
                                />
                            </Form.Group>
                        </Row>
                        <Row className="mb-2">
                            <Form.Group as={Col} controlId="benefits">
                                <Form.Label>Benefits</Form.Label>
                                <CreatableSelect
                                    isMulti
                                    isDisabled={!props.editable || showToast}
                                    value={request?.benefits ? request?.benefits.map(v => ({label:v, value:v})) : []}
                                    onChange={(v) => changeValue({currentTarget: { id: 'benefits', value: v}})}
                                    name="benefitsOptions"
                                    options={benefits}
                                    className="basic-multi-select"
                                    classNamePrefix="select"
                                    styles={customStyles}
                                />
                            </Form.Group>
                        </Row>
                        <Row className="mb-2">
                            <Form.Group as={Col} controlId="panel1">
                                <Form.Label>Interviewer 1</Form.Label>
                                <AsyncSelect
                                    isClearable
                                    cacheOptions
                                    isDisabled={!props.editable || showToast}
                                    classNamePrefix="select"
                                    styles={customStyles}
                                    loadOptions={(input) => debouncedLoadUsers(input, request.panel2)}
                                    onChange={(v) => changeValue({currentTarget: { id: 'panel1', value: v}})}
                                    placeholder={'Type a name'}
                                    value={cleanNameFromIdentity(request?.panel1)}
                                />
                            </Form.Group>
                            <Form.Group as={Col} controlId="panel2">
                                <Form.Label>Interviewer 2</Form.Label>
                                <AsyncSelect
                                    isClearable
                                    cacheOptions
                                    isDisabled={!props.editable || showToast}
                                    classNamePrefix="select"
                                    styles={customStyles}
                                    loadOptions={(input) => debouncedLoadUsers(input, request.panel1)}
                                    onChange={(v) => changeValue({currentTarget: { id: 'panel2', value: v}})}
                                    placeholder={'Type a name'}
                                    value={cleanNameFromIdentity(request?.panel2)}
                                />
                            </Form.Group>
                        </Row>  
                        {props.editable && !showToast && (
                            <Button className="updateUser" variant="success" type="submit">Request</Button>
                        )}
                    </Modal.Body>
                </Form>
                <ToastContainer className="position-absolute p-3 top-50 start-50 translate-middle" >
                    <Toast onClose={() => setShowToast(false)} show={showToast} delay={3000} bg={messageAPI.substr(0, 6) !== 'Error,' ? 'light' : 'warning'} autohide={messageAPI.substr(0, 6) !== 'Error,'} >
                        <Toast.Header>
                            {messageAPI.substr(0, 6) === 'Error,' && <strong>Something went wrong</strong>}
                        </Toast.Header>
                        <Toast.Body>{messageAPI}</Toast.Body>
                    </Toast>
                </ToastContainer>
            </Modal>
        </>
    );
};